import {Detail} from '@forecast-it/design-system';
import {selectUnit} from '@formatjs/intl-utils';
import Moment from 'moment';
import React from 'react';
import {FormattedHTMLMessage, useIntl} from 'react-intl';
import {INITIALS_SIZE} from '../../../../constants';
import PersonInitials from '../person/person_initials';
import {DeprecatedProjectIndicatorComponent} from '../project-indicator/DeprecatedProjectIndicator';
import {initialsFor} from './initialsFor';
import type {NovaNotificationEntry} from './novaNotificationStore';

export interface NovaNotificationRowProps {
	entry: NovaNotificationEntry;
	onOpenTask: (taskId: string) => void;
	onDismiss: (id: string) => void;
}

// Resets native <button> chrome only — layout, spacing, typography, and background all come from
// the real .notification/.notification-icon/.notification-text-wrapper classes below
// (notification.scss), not from anything declared here. Deliberately does not set `display` or
// `background`: `.notification` already declares `display: flex` and its own `&.unread`/`:hover`
// background-color rules, and any inline value for either would win over them (an inline style
// always beats a class for the same property), leaving the row plain white regardless of the
// unread class — exactly the bug a real side-by-side comparison against a native row caught.
// `appearance: none` removes the browser's own button skin without touching background.
const ROW_BUTTON_STYLE: React.CSSProperties = {
	width: '100%',
	border: 'none',
	appearance: 'none',
	padding: 0,
	textAlign: 'left',
	cursor: 'pointer',
	font: 'inherit',
};

// Matches Notification.js's own stripHtml — FormattedHTMLMessage's template carries literal <b>
// tags for the design-system's benefit; an aria-label needs the plain sentence instead.
function stripHtml(html: string): string {
	return html.replace(/<b>|<\/b>/g, '');
}

// Matches Notification.js's own threshold: under a month old renders as a relative time; at or
// past a month, a relative count stops being useful and this switches to an absolute date instead.
function formatTimestamp(createdAt: number, formatRelativeTime: ReturnType<typeof useIntl>['formatRelativeTime']): string {
	const {value, unit} = selectUnit(createdAt);
	if (unit === 'month' || unit === 'year') {
		return Moment(createdAt).format('LL');
	}
	return formatRelativeTime(unit === 'second' && value > -10 ? 0 : value, unit, {numeric: 'auto'});
}

// Renders one Nova notification, whether shown standalone or merged into the real
// NotificationContainer's own list (see NotificationContainer.js's isNovaNotification branch).
//
// Reuses the real .notification/.notification-icon/.notification-text-wrapper/.notification-text/
// .bottom-row/.timestamp classes (notification.scss) rather than an independent FlexRow/FlexColumn
// layout with approximated spacing values — a real, live comparison (Nova and native rows sitting
// in the same merged list) showed the two visibly misaligned: the native icon gutter is a fixed
// 70px box with 23px/16px padding, not a small flex gap, and .bottom-row is plain flex-start, not
// space-between. Sharing the actual classes makes the two kinds of row match by construction, and
// keeps matching automatically if notification.scss's spacing ever changes.
export const NovaNotificationRow = (props: NovaNotificationRowProps): JSX.Element => {
	const {formatMessage, formatRelativeTime} = useIntl();
	const {entry} = props;

	if (entry.kind === 'overflow_summary') {
		return (
			<div data-cy={`nova-notification-${entry.id}`} className="notification unread">
				<div className="notification-icon" />
				<div className="notification-text-wrapper">
					<div className="notification-text">
						<Detail color="medium">
							{formatMessage({id: 'nova_invocation.notifications.overflow'}, {count: entry.count})}
						</Detail>
					</div>
				</div>
			</div>
		);
	}

	// The task id shown to the user (e.g. "T58785") is not always known — same optionality as
	// taskUrl (see novaNotificationStore's own comment) — so the clause referencing it is dropped
	// rather than left blank when absent.
	const taskReference = entry.companyTaskId ? `on T${entry.companyTaskId} — ` : '';
	const messageId =
		entry.kind === 'succeeded' ? 'nova_invocation.notifications.succeeded' : 'nova_invocation.notifications.failed';
	const values = {taskReference, taskName: entry.taskName};
	const plainPrimaryText = stripHtml(formatMessage({id: messageId}, values));
	const metaText = `${entry.agentName} · ${formatTimestamp(entry.createdAt, formatRelativeTime)}`;

	return (
		<button
			type="button"
			className="notification unread"
			data-cy={`nova-notification-${entry.id}`}
			aria-label={`${plainPrimaryText} ${metaText}`}
			onClick={() => {
				props.onOpenTask(entry.taskId);
				props.onDismiss(entry.id);
			}}
			style={ROW_BUTTON_STYLE}
		>
			<div className="notification-icon">
				<div className="hexagon">
					<PersonInitials initials={initialsFor(entry.agentName)} size={INITIALS_SIZE.LARGE} />
				</div>
			</div>
			<div className="notification-text-wrapper">
				<div className="notification-text">
					<FormattedHTMLMessage id={messageId} values={values} />
				</div>
				<div className="bottom-row">
					{entry.companyProjectId ? (
						<DeprecatedProjectIndicatorComponent
							companyProjectId={Number(entry.companyProjectId)}
							customProjectId={null}
							projectColor={entry.projectColor ?? null}
							disableLink
						/>
					) : null}
					<div className="timestamp">{metaText}</div>
				</div>
			</div>
		</button>
	);
};
